import { LoaderCircle, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildAddWidgetPrompt, harvestWidgets } from './reportBuild';
import { Button } from '@/ui/design-system/components/ui/button';
import { Text } from '@/ui/design-system/components';
import { createReportChatTransport } from './reportChat';
import { lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useAuth0 } from '@auth0/auth0-react';
import { useChat } from '@ai-sdk/react';
import useCountryParam from '@/utils/hooks/useCountryParam';
import useSearchableIndustries from '@/utils/hooks/useSearchableIndustries';

// One in-flight "add a widget" build, rendered as a tile in the report grid.
// Unlike useAddWidget (one chat shared by the add panel / edit modal, fired on
// demand), each PendingWidgetBuild owns its OWN chat and fires once on mount — so
// the add panel stays usable and the user can queue several builds that run
// concurrently, each as its own loading tile. On settle it hands the harvested
// widget(s) to onComplete (the report appends them) and unmounts; a build that
// errors or produces nothing renderable shows a dismissable hint instead.
// Colocated with the Reports views.
function PendingWidgetBuild({ text, onComplete, onDismiss }) {
    const { getAccessTokenSilently } = useAuth0();
    const { currentCountryParam } = useCountryParam();

    const geoRef = useRef(currentCountryParam);
    geoRef.current = currentCountryParam;

    // No composer here, so widget adds always run on the auto model tier.
    const modelTierRef = useRef(null);

    const industries = useSearchableIndustries();
    const industriesRef = useRef(industries);
    industriesRef.current = industries;

    const transport = useMemo(
        () =>
            createReportChatTransport({
                getAccessTokenSilently,
                geoRef,
                modelTierRef,
                industriesRef,
            }),
        [getAccessTokenSilently],
    );

    const { messages, sendMessage, status } = useChat({
        transport,
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    });

    const isBuilding = status === 'streaming' || status === 'submitted';
    // null while building; 'empty' (no renderable widget) or 'error' once settled
    // without a result — both render a dismissable hint rather than vanishing.
    const [outcome, setOutcome] = useState(null);

    // Send this build's request exactly once.
    const sentRef = useRef(false);
    useEffect(() => {
        if (sentRef.current) return;
        sentRef.current = true;
        sendMessage({ text: buildAddWidgetPrompt(text) });
    }, [sendMessage, text]);

    // Harvest on the building→settled transition. Read messages/onComplete from
    // refs so the effect can depend only on the status transition.
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;
    const prevBuildingRef = useRef(isBuilding);
    useEffect(() => {
        const wasBuilding = prevBuildingRef.current;
        prevBuildingRef.current = isBuilding;
        if (!(wasBuilding && !isBuilding)) return;
        if (status === 'error') {
            setOutcome('error');
            return;
        }
        const widgets = harvestWidgets(messagesRef.current);
        if (widgets.length > 0) onCompleteRef.current(widgets);
        else setOutcome('empty');
    }, [isBuilding, status]);

    if (outcome) {
        return (
            <div className="min-h-[160px] flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-background-med-em p-4 text-center">
                <Text size="sm" className="text-red-600">
                    {outcome === 'error'
                        ? 'Something went wrong building that widget.'
                        : "Couldn't build a widget from that — try rephrasing."}
                </Text>
                <Text size="xs" color="muted" className="line-clamp-2">
                    “{text}”
                </Text>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={onDismiss}
                >
                    <X size={14} />
                    Dismiss
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-[160px] flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-background-med-em p-4 text-center">
            <div className="flex items-center gap-2 text-muted-foreground">
                <LoaderCircle size={18} className="animate-spin" />
                <Text size="sm" color="muted">
                    Building widget…
                </Text>
            </div>
            {/* Echo the request so concurrent build tiles stay distinguishable. */}
            <Text size="xs" color="muted" className="line-clamp-2">
                “{text}”
            </Text>
        </div>
    );
}

export default PendingWidgetBuild;
